import { ref, reactive, nextTick } from 'vue'

// Global notification state
const notifications = ref([])

// Counter for unique IDs
let notificationIdCounter = 0

/**
 * useNotifications composable
 * Provides methods to show, dismiss, and manage notifications
 */
export function useNotifications() {

  /**
   * Add a new notification
   * @param {Object} options - Notification options
   * @param {string} options.type - Type: 'success', 'error', 'warning', 'info'
   * @param {string} options.title - Optional title
   * @param {string} options.message - Notification message
   * @param {number} options.duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
   * @param {boolean} options.dismissible - Whether notification can be manually dismissed
   * @param {Array} options.actions - Optional action buttons
   * @param {Object} options.data - Additional data to store with notification
   * @returns {string} Notification ID
   */
  const addNotification = (options) => {
    const notification = reactive({
      id: `notification-${++notificationIdCounter}`,
      type: options.type || 'info',
      title: options.title || '',
      message: options.message || '',
      duration: options.duration !== undefined ? options.duration : 5000,
      dismissible: options.dismissible !== false,
      actions: options.actions || null,
      data: options.data || {},
      paused: false,
      timer: null,
      createdAt: Date.now()
    })

    notifications.value.push(notification)

    // Set up auto-dismiss timer
    if (notification.duration > 0) {
      notification.timer = setTimeout(() => {
        dismissNotification(notification.id)
      }, notification.duration)
    }

    return notification.id
  }

  /**
   * Dismiss a notification by ID
   * @param {string} id - Notification ID
   */
  const dismissNotification = (id) => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      const notification = notifications.value[index]

      // Clear timer if it exists
      if (notification.timer) {
        clearTimeout(notification.timer)
      }

      notifications.value.splice(index, 1)
    }
  }

  /**
   * Dismiss all notifications
   */
  const dismissAll = () => {
    // Clear all timers
    notifications.value.forEach(notification => {
      if (notification.timer) {
        clearTimeout(notification.timer)
      }
    })

    notifications.value.length = 0
  }

  /**
   * Pause auto-dismiss for a notification
   * @param {string} id - Notification ID
   */
  const pauseNotification = (id) => {
    const notification = notifications.value.find(n => n.id === id)
    if (notification && notification.timer) {
      clearTimeout(notification.timer)
      notification.paused = true
    }
  }

  /**
   * Resume auto-dismiss for a notification
   * @param {string} id - Notification ID
   */
  const resumeNotification = (id) => {
    const notification = notifications.value.find(n => n.id === id)
    if (notification && notification.paused && notification.duration > 0) {
      notification.paused = false
      // Resume with remaining time (simplified - using original duration)
      notification.timer = setTimeout(() => {
        dismissNotification(id)
      }, notification.duration)
    }
  }

  /**
   * Update a notification
   * @param {string} id - Notification ID
   * @param {Object} updates - Updates to apply
   */
  const updateNotification = (id, updates) => {
    const notification = notifications.value.find(n => n.id === id)
    if (notification) {
      Object.assign(notification, updates)
    }
  }

  // Convenience methods for different notification types

  /**
   * Show success notification
   * @param {string|Object} messageOrOptions - Message string or options object
   * @param {Object} options - Additional options (if first param is string)
   * @returns {string} Notification ID
   */
  const success = (messageOrOptions, options = {}) => {
    if (typeof messageOrOptions === 'string') {
      return addNotification({
        type: 'success',
        message: messageOrOptions,
        ...options
      })
    }
    return addNotification({ type: 'success', ...messageOrOptions })
  }

  /**
   * Show error notification
   * @param {string|Object} messageOrOptions - Message string or options object
   * @param {Object} options - Additional options (if first param is string)
   * @returns {string} Notification ID
   */
  const error = (messageOrOptions, options = {}) => {
    if (typeof messageOrOptions === 'string') {
      return addNotification({
        type: 'error',
        message: messageOrOptions,
        duration: 0, // Don't auto-dismiss errors by default
        ...options
      })
    }
    return addNotification({
      type: 'error',
      duration: 0,
      ...messageOrOptions
    })
  }

  /**
   * Show warning notification
   * @param {string|Object} messageOrOptions - Message string or options object
   * @param {Object} options - Additional options (if first param is string)
   * @returns {string} Notification ID
   */
  const warning = (messageOrOptions, options = {}) => {
    if (typeof messageOrOptions === 'string') {
      return addNotification({
        type: 'warning',
        message: messageOrOptions,
        duration: 7000, // Longer duration for warnings
        ...options
      })
    }
    return addNotification({
      type: 'warning',
      duration: 7000,
      ...messageOrOptions
    })
  }

  /**
   * Show info notification
   * @param {string|Object} messageOrOptions - Message string or options object
   * @param {Object} options - Additional options (if first param is string)
   * @returns {string} Notification ID
   */
  const info = (messageOrOptions, options = {}) => {
    if (typeof messageOrOptions === 'string') {
      return addNotification({
        type: 'info',
        message: messageOrOptions,
        ...options
      })
    }
    return addNotification({ type: 'info', ...messageOrOptions })
  }

  /**
   * Show loading notification with optional promise handling
   * @param {string} message - Loading message
   * @param {Promise} promise - Optional promise to handle
   * @param {Object} options - Additional options
   * @returns {string} Notification ID or Promise if promise provided
   */
  const loading = (message, promise = null, options = {}) => {
    const id = addNotification({
      type: 'info',
      message: message,
      duration: 0, // Don't auto-dismiss loading notifications
      dismissible: false,
      ...options
    })

    if (promise) {
      return promise
        .then((result) => {
          dismissNotification(id)
          success(options.successMessage || 'Operation completed successfully')
          return result
        })
        .catch((error) => {
          dismissNotification(id)
          const errorMessage = options.errorMessage || 'Operation failed'
          error(typeof error === 'string' ? error : errorMessage)
          throw error
        })
    }

    return id
  }

  /**
   * Show a confirmation notification with actions
   * @param {Object} options - Confirmation options
   * @param {string} options.message - Confirmation message
   * @param {Function} options.onConfirm - Callback for confirm action
   * @param {Function} options.onCancel - Callback for cancel action
   * @param {string} options.confirmText - Text for confirm button
   * @param {string} options.cancelText - Text for cancel button
   * @returns {string} Notification ID
   */
  const confirm = (options) => {
    const {
      message,
      title = 'Confirm Action',
      onConfirm,
      onCancel,
      confirmText = 'Confirm',
      cancelText = 'Cancel'
    } = options

    return addNotification({
      type: 'warning',
      title,
      message,
      duration: 0, // Don't auto-dismiss
      actions: [
        {
          label: cancelText,
          type: 'default',
          onClick: () => {
            if (onCancel) onCancel()
          }
        },
        {
          label: confirmText,
          type: 'primary',
          onClick: () => {
            if (onConfirm) onConfirm()
          }
        }
      ]
    })
  }

  /**
   * Get notification count by type
   * @param {string} type - Notification type
   * @returns {number} Count
   */
  const getCount = (type = null) => {
    if (!type) return notifications.value.length
    return notifications.value.filter(n => n.type === type).length
  }

  /**
   * Check if there are any notifications
   * @returns {boolean} Has notifications
   */
  const hasNotifications = () => {
    return notifications.value.length > 0
  }

  return {
    // State
    notifications,

    // Methods
    addNotification,
    dismissNotification,
    dismissAll,
    pauseNotification,
    resumeNotification,
    updateNotification,

    // Convenience methods
    success,
    error,
    warning,
    info,
    loading,
    confirm,

    // Utility methods
    getCount,
    hasNotifications
  }
}

// Global instance for easy access
export const notificationService = useNotifications()

// Export for direct use in templates
export default useNotifications